import CompanyAPI from 'dashboard/api/companies';
import { createStore } from 'dashboard/store/helpers/storeFactory';
import camelcaseKeys from 'camelcase-keys';

export default createStore({
  name: 'Company',
  API: CompanyAPI,
  getters: {
    getCompaniesList: state => {
      return camelcaseKeys(state.records, { deep: true });
    },
    getCompanyById: state => id => {
      const company = state.records.find(c => c.id === Number(id));
      return company ? camelcaseKeys(company, { deep: true }) : null;
    },
    // getMeta and getUIFlags are provided by storeFactory defaults
  },
  actions: mutationTypes => ({
    search: async ({ commit }, { search, page, sort }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await CompanyAPI.search(search, page, sort);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
      } catch {
        // Error silently handled
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    fetchFiltered: async ({ commit }, filters = {}) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await CompanyAPI.get(filters);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    getCompany: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { fetching: true });
      try {
        const { data } = await CompanyAPI.getCompany(id);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetching: false });
      }
    },
    createCompany: async ({ commit }, companyData) => {
      commit(mutationTypes.SET_UI_FLAG, { creating: true });
      try {
        const { data } = await CompanyAPI.create(companyData);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { creating: false });
      }
    },
    updateCompany: async ({ commit }, { id, ...companyData }) => {
      commit(mutationTypes.SET_UI_FLAG, { updating: true });
      try {
        const { data } = await CompanyAPI.update(id, companyData);
        commit(mutationTypes.EDIT, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { updating: false });
      }
    },
    deleteCompany: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { deleting: true });
      try {
        await CompanyAPI.delete(id);
        commit(mutationTypes.DESTROY, id);
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { deleting: false });
      }
    },
    // Alias for consistency with other stores
    show: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingItem: true });
      try {
        const { data } = await CompanyAPI.getCompany(id);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingItem: false });
      }
    },
    update: async ({ commit }, { id, ...companyData }) => {
      commit(mutationTypes.SET_UI_FLAG, { updating: true });
      try {
        const { data } = await CompanyAPI.update(id, companyData);
        commit(mutationTypes.EDIT, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { updating: false });
      }
    },
    delete: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { deleting: true });
      try {
        await CompanyAPI.delete(id);
        commit(mutationTypes.DESTROY, id);
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { deleting: false });
      }
    },
    getContacts: async (_, { companyId, page = 1 }) => {
      try {
        const { data } = await CompanyAPI.getContacts(companyId, page);
        return data.payload || data;
      } catch {
        return [];
      }
    },
    getDeals: async (_, { companyId, page = 1 }) => {
      try {
        const { data } = await CompanyAPI.getDeals(companyId, page);
        return data.payload || data;
      } catch {
        return [];
      }
    },
    getActivities: async (_, { companyId, page = 1 }) => {
      try {
        const { data } = await CompanyAPI.getActivities(companyId, page);
        return data.payload || data;
      } catch {
        return [];
      }
    },
    matchByEmail: async (_, email) => {
      try {
        const { data } = await CompanyAPI.matchByEmail(email);
        return data.company;
      } catch {
        return null;
      }
    },
  }),
});
